import LegalPageLayout from "@/components/legal/LegalPageLayout";

const Terms = () => {
  return (
    <LegalPageLayout title="Terms and Conditions" effectiveDate="April 30, 2026" version="1.2">
      <h2>1. Agreement to Terms</h2>
      <p>
        These Terms and Conditions ("Terms") form a binding agreement between you and Young's Digital
        Atelier LLC, a subsidiary of Young's Multimedia Holdings LLC ("Wavlynq," "we," "us"). By
        creating an account or using Wavlynq or any of its sibling apps — currently Fryqnc, Pulsynq,
        and MindLynq, with Spiritsynq, Jadesynq, Deafwav, Ablelynq, Fullynq, and Openlynq launching
        next (collectively, the "Services") — you agree to these Terms, our{" "}
        <a href="/privacy">Privacy Policy</a>, our <a href="/cookies">Cookie Policy</a>, and our{" "}
        <a href="/ai-policy">AI Use Policy</a>.
      </p>
      <p>
        Wavlynq is the umbrella portal — not an app you join. Each sibling app is a stand-alone
        community with its own identity surface, mentor program, events, and crisis resources.
      </p>
      <p>
        <strong>Section 16 contains a binding arbitration clause and a class-action waiver.</strong>{" "}
        Read it carefully.
      </p>

      <h2>2. Eligibility</h2>
      <p>
        You must be <strong>at least 18 years old</strong> to use the Services. By creating an account
        you represent that you are 18 or older, that you have the legal capacity to enter this
        agreement, and that your use of the Services does not violate any law in your jurisdiction.
      </p>

      <h2>3. Your Account</h2>
      <ul>
        <li>You are responsible for activity on your account and for keeping your credentials secure.</li>
        <li>
          You may sign up with email and password, Google, Apple (where available), or your phone
          number via SMS one-time-code. All account-related communication may also be delivered by
          email or SMS where you have provided that contact method.
        </li>
        <li>You must provide accurate information, including a real date of birth and legal name where required.</li>
        <li>You may have one account per app per person. Impersonation, fake accounts, and shared accounts are prohibited.</li>
        <li>We may suspend or terminate accounts that violate these Terms or pose a risk to the community.</li>
      </ul>

      <h2>4. Subscriptions, Trials, and À la Carte</h2>
      <p>
        Wavlynq offers five subscription tiers:
      </p>
      <ul>
        <li><strong>Signal</strong> — free.</li>
        <li><strong>Pulse</strong> — $9.99/mo or $99/yr.</li>
        <li><strong>Surge</strong> — $19.99/mo or $199/yr.</li>
        <li><strong>Spectrum</strong> — $29.99/mo or $299/yr.</li>
        <li><strong>Attuned</strong> — $99.99/mo or $999/yr. Attuned is the only tier that grants cross-app access across every sibling app.</li>
      </ul>
      <p>
        À la carte add-ons are sold separately and listed in-app on the À la carte page. Some add-ons
        are gated to specific apps or tiers — for example, RESONATE is available only on MindLynq,
        and Incognito Mode is provided free of charge despite being listed.
      </p>
      <ul>
        <li><strong>Trial:</strong> Pulse, Surge, Spectrum, and Attuned each include a 7-day free trial. You will be charged at the end of the trial unless you cancel before it ends.</li>
        <li><strong>Auto-renewal:</strong> Subscriptions renew automatically until cancelled. Cancel anytime in Settings &gt; Billing or via the Stripe Customer Portal.</li>
        <li><strong>Refunds:</strong> Except where required by law, fees are non-refundable. One-time à la carte purchases are non-refundable once activated.</li>
        <li><strong>Price changes:</strong> We may change prices with at least 30 days' notice; the new price applies at your next renewal.</li>
      </ul>

      <h2>5. Identity Verification (KYC)</h2>
      <p>
        To protect the community we may ask you to verify your identity. Verification is provided by{" "}
        <strong>Stripe Identity</strong> (our primary provider), with Veriff, Onfido, or Persona used
        as fallback where Stripe Identity is unavailable. Verification typically requires a
        government-issued ID and a selfie. The result (pass / fail / pending) is stored on your
        account; we do not retain raw ID images beyond what the verification provider returns to us.
        Mentors are subject to a separate, stricter background check (see Section 9).
      </p>

      <h2>6. SMS Program — Wavlynq SMS Notifications</h2>
      <p>
        <strong>Program name:</strong> Wavlynq SMS Notifications.<br />
        <strong>Program description:</strong> Account verification codes (including phone sign-in),
        login alerts, password resets, match notifications, message alerts, event RSVP confirmations
        and reminders, mentor session reminders, and subscription billing notices.<br />
        <strong>Message frequency:</strong> Varies based on your activity.<br />
        <strong>Message and data rates may apply.</strong>
      </p>
      <p>
        <strong>To opt out, reply STOP to any message.</strong><br />
        <strong>For help, reply HELP or email support@wavlynq.com.</strong>
      </p>
      <p>
        Auth-related SMS (verification codes, login alerts) is transactional and required for account
        security; if you opt out of SMS you must use email-based authentication instead. See our{" "}
        <a href="/privacy">Privacy Policy</a> for how we handle phone numbers. We do not share mobile
        phone numbers or SMS opt-in information with third parties or affiliates for marketing
        purposes.
      </p>

      <h2>7. Acceptable Use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Harass, threaten, stalk, dox, or discriminate against any user.</li>
        <li>Post sexually explicit content, content involving minors, content depicting non-consensual acts, or any content prohibited by law.</li>
        <li>Impersonate another person or misrepresent your identity, age, or affiliation.</li>
        <li>Use the Services for commercial solicitation, spam, or unauthorized advertising.</li>
        <li>
          Attempt to bypass safety features (Crisis Resources, the Emergency Button, Block, Report,
          Incognito, per-app identity walls).
        </li>
        <li>Scrape, reverse-engineer, or interfere with the Services.</li>
        <li>Use automated tools, bots, or AI to interact with other members in a way that misrepresents you as human.</li>
        <li>Sell, trade, or transfer your account.</li>
      </ul>

      <h2>8. Community Events and Hosted Meetups</h2>
      <p>
        Wavlynq-hosted events and Community-Hosted events both require barcode entry, physical ID
        check at the door, and pre-registration of guests. <strong>These safety requirements apply
        equally to community-hosted events.</strong> Hosts using the Event Host Pass remain bound by
        all safety rules and are responsible for compliance with local laws. Approved hosts are paid
        out via <strong>Stripe Connect</strong>; payout terms are presented at onboarding.
      </p>

      <h2>9. Mentor Program</h2>
      <p>
        Mentors in any sibling app must complete a mandatory background check and ongoing
        community-standards training. Mentor status may be revoked at any time for safety reasons.
      </p>

      <h2>10. Safety Features and Emergency Button</h2>
      <p>
        Every sibling app includes the <strong>Emergency Button</strong>, persistent Crisis
        Resources, and a public <strong>Safety Center</strong>. The Emergency Button's appearance is
        member-configurable but its function is hardcoded and locale-aware (it dials the appropriate
        local emergency number and, where you have enabled it, can begin a recording). A recording
        started by the Emergency Button can only be stopped by the pressing member or by Wavlynq's
        founder contact (jyoung@youngsmultimedia.com). Crisis-aware behavior in our RAELA AI
        assistant is always on and cannot be disabled by any user, admin, or setting.
      </p>

      <h2>11. User Content</h2>
      <p>
        You retain ownership of content you post. You grant Wavlynq a worldwide, non-exclusive,
        royalty-free license to host, store, reproduce, modify (for formatting), display, and
        distribute your content solely to operate and improve the Services. This license ends when
        you delete the content, except for backups and content already shared with other members.
      </p>
      <p>
        You represent that you have all necessary rights to the content you post and that it does
        not violate any law or third-party right.
      </p>

      <h2>12. Intellectual Property</h2>
      <p>
        Wavlynq and the names, logos, and software of all sibling apps (including Fryqnc, Pulsynq,
        MindLynq, Spiritsynq, Jadesynq, Deafwav, Ablelynq, Fullynq, and Openlynq) are owned by or
        licensed to Young's Digital Atelier LLC. Nothing in these Terms grants you any right to our
        trademarks or proprietary technology beyond the limited license to use the Services.
      </p>

      <h2>13. Third-Party Services</h2>
      <p>
        The Services use third-party providers including Lovable Cloud (our managed backend), Stripe
        (payments, Customer Portal, Connect for host payouts, and Stripe Identity for KYC), Twilio
        (SMS), Google and Apple (sign-in), Resend (transactional and auth email), Mapbox (maps and
        place search), the technology providers behind our RAELA AI assistant, and background-check
        providers. Their terms apply to their services. We are not responsible for third-party
        content or services.
      </p>

      <h2>14. Disclaimers</h2>
      <p>
        THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS
        OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
        NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, SECURE, OR
        ERROR-FREE, OR THAT ANY MATCH, EVENT, OR INTERACTION WILL MEET YOUR EXPECTATIONS.
      </p>
      <p>
        <strong>Wavlynq is not responsible for the conduct of any user, online or offline.</strong>{" "}
        You are solely responsible for your interactions with other members.
      </p>

      <h2>15. Limitation of Liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, WAVLYNQ AND ITS AFFILIATES, OFFICERS, EMPLOYEES, AND
        AGENTS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY,
        OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, GOODWILL, OR OTHER INTANGIBLE LOSSES.
        OUR TOTAL LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATING TO THE SERVICES WILL NOT EXCEED
        THE GREATER OF (A) THE AMOUNTS YOU PAID US IN THE 12 MONTHS BEFORE THE CLAIM AROSE, OR
        (B) ONE HUNDRED U.S. DOLLARS ($100).
      </p>

      <h2>16. Indemnification</h2>
      <p>
        You agree to indemnify and hold harmless Wavlynq, Young's Digital Atelier LLC, Young's
        Multimedia Holdings LLC, and their respective officers, directors, employees, and agents
        from any claims, damages, liabilities, costs, and expenses (including reasonable attorneys'
        fees) arising from your use of the Services, your content, or your violation of these Terms.
      </p>

      <h2>17. Termination</h2>
      <p>
        You may delete your account at any time. We may suspend or terminate your access for
        violations of these Terms or for any reason that protects the safety of the community.
        Sections 11–19 survive termination.
      </p>

      <h2>18. Binding Arbitration and Class-Action Waiver</h2>
      <p>
        <strong>PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS.</strong>
      </p>
      <p>
        <strong>18.1 Arbitration.</strong> Any dispute, claim, or controversy between you and
        Wavlynq arising out of or relating to these Terms or the Services will be resolved by{" "}
        <strong>binding individual arbitration</strong> administered by the American Arbitration
        Association under its Consumer Arbitration Rules. Arbitration will take place in Bexar
        County, Texas, or by videoconference at your election. Judgment on the award may be entered
        in any court of competent jurisdiction.
      </p>
      <p>
        <strong>18.2 Class-Action Waiver.</strong> You and Wavlynq agree that disputes will be
        brought in an individual capacity only, and not as a plaintiff or class member in any
        purported class, collective, consolidated, or representative action.
      </p>
      <p>
        <strong>18.3 Exceptions.</strong> Either party may bring an individual action in small-claims
        court. Either party may seek injunctive relief in court for intellectual-property
        infringement.
      </p>
      <p>
        <strong>18.4 Opt-Out.</strong> You may opt out of this arbitration agreement by emailing{" "}
        <a href="mailto:legal@wavlynq.com">legal@wavlynq.com</a> within 30 days of first accepting
        these Terms with the subject line "Arbitration Opt-Out" and your full legal name and account
        email.
      </p>

      <h2>19. Governing Law and Venue</h2>
      <p>
        These Terms are governed by the laws of the <strong>State of Texas, USA</strong>, without
        regard to conflict-of-laws rules. For any matter not subject to arbitration, the exclusive
        venue is the state and federal courts located in Bexar County, Texas, and you consent to
        personal jurisdiction there.
      </p>

      <h2>20. Changes to these Terms</h2>
      <p>
        We may update these Terms. Material changes will be announced in-app or by email at least 14
        days before they take effect. Continued use after the effective date constitutes acceptance.
      </p>

      <h2>21. Contact</h2>
      <p>
        Young's Digital Atelier LLC<br />
        702 Richland Hills Dr #760866<br />
        San Antonio, TX 78245, USA<br />
        Legal: <a href="mailto:legal@wavlynq.com">legal@wavlynq.com</a><br />
        Support: <a href="mailto:support@wavlynq.com">support@wavlynq.com</a>
      </p>
    </LegalPageLayout>
  );
};

export default Terms;
